import Group from 'components/utils/Group'
import { useAppSelector } from 'hooks/reduxHooks'
import { useDirectSearchPrices } from 'hooks/Search/useSearchPrices'
import useBedbankOfferSearchPricing from 'hooks/useBedbankOfferSearchPricing'
import { EmptyObject } from 'lib/object/objectUtils'
import getPrioritizedBedbankAmenities from 'lib/offer/getPrioritizedBedbankAmenities'
import LuxLPPPricingLabeledSheet from 'luxPlus/components/LuxLPPPricingLabeledSheet'
import { getIsLuxPlusLPPEnabled } from 'luxPlus/selectors/featureToggle'
import { useMemo } from 'react'
import SearchTileStackAmenitiesList from '../shared/SearchTileStackAmenitiesList'
import SearchTileRoomBreakdown from '../shared/SearchTileRoomBreakdown'
import { useActiveAmenityFilters } from 'hooks/useActiveAmenityFilters'
import { reorderAmenitiesByActiveFilters } from 'lib/offer/amenitiesUtils'
import LoyaltyInclusions from 'loyalty/components/LoyaltyInclusions'

interface Props {
  offer: App.BedbankOffer | App.BedbankOfferSummary
  filters?: App.OfferListFilters
  truncateAfter?: number
  singleColumn?: boolean
  hideLuxPlusPricingSheet?: boolean
  hideRoomBreakdown?: boolean
  maxAmenitiesToShow?: number
}

function BedbankSearchTileFeatures({
  offer,
  filters = EmptyObject,
  truncateAfter,
  singleColumn,
  hideLuxPlusPricingSheet = false,
  hideRoomBreakdown = false,
  maxAmenitiesToShow,
}: Props) {
  const activeAmenityFilters = useActiveAmenityFilters()
  const { offerMemberPrice, offerPrice, shownRate } = useBedbankOfferSearchPricing(offer, filters)
  const amenities = useMemo<Array<App.OfferInclusion>>(() => {
    // Reorder popularFacilities before mapping to boost matching amenities
    const reorderedFacilities = activeAmenityFilters.size > 0
      ? reorderAmenitiesByActiveFilters(offer.popularFacilities, activeAmenityFilters)
      : offer.popularFacilities

    return getPrioritizedBedbankAmenities({
      shownRateInclusions: shownRate?.inclusions,
      popularFacilities: reorderedFacilities,
    })
  }, [offer.popularFacilities, shownRate?.inclusions, activeAmenityFilters])
  const truncateAmenitiesAfter = maxAmenitiesToShow ?? truncateAfter

  const luxPlusLPPEnabled = useAppSelector(getIsLuxPlusLPPEnabled)
  const directSearchPrices = useDirectSearchPrices({ filters, offerId: offer.id })
  return <Group direction="vertical" verticalAlign="space-between" gap={12} grow noDisplayWhenEmpty>
    <Group direction="vertical" gap={12} noDisplayWhenEmpty>
      {luxPlusLPPEnabled && !hideLuxPlusPricingSheet && <LuxLPPPricingLabeledSheet
        offer={offer}
        hasLuxPlusRate={directSearchPrices?.hasLuxPlusRate}
        offerMemberPrice={offerMemberPrice}
        offerPrice={offerPrice}
      />}
      <Group direction="vertical" gap={4} noDisplayWhenEmpty>
        {!!amenities.length && <SearchTileStackAmenitiesList amenities={amenities} truncateAfter={truncateAmenitiesAfter} singleColumn={singleColumn} />}
        <LoyaltyInclusions offer={offer} variant="search-tile" />
      </Group>
    </Group>
    {!hideRoomBreakdown && <SearchTileRoomBreakdown
      offer={offer}
      filters={filters}
    />}
  </Group>
}

export default BedbankSearchTileFeatures
